import {
  AlcoholIcon,
  BarrelIcon,
  CellaringIcon,
  ClosureIcon,
  MoleculeIcon,
  RegionIcon,
  ShakerIcon,
  TempIcon,
  VarietalIcon,
  VolumeIcon,
} from "@vinomofo/icons";
import { Grid, Heading, Section, Stack, Text } from "@vinomofo/components";
import type { Region, WineAttributes } from "../../types";

import { WineNerdBadge } from "../wine-nerd-badge";

export interface WineWineNerdProps extends WineAttributes {
  heading: string;
  subheading: string;
  region: string;
  volume: string;
  isSpirit?: boolean;
}

type Item = {
  key: string;
  value: (v: string | string[] | number | Region) => string;
  heading: string;
  icon: JSX.Element;
};

const items: Item[] = [
  {
    key: "vintage",
    value: (value: string): string => value || "-",
    heading: "Vintage",
    icon: <VarietalIcon />,
  },
  {
    key: "region",
    value: (value: string): string => value || "-",
    heading: "Region",
    icon: <RegionIcon />,
  },
  {
    key: "cellaring",
    value: (value: string): string => value || "-",
    heading: "Cellaring",
    icon: <CellaringIcon />,
  },
  {
    key: "preservatives",
    value: (value: string[]): string =>
      value.length > 0 ? value.join(", ") : "-",
    heading: "Preservatives",
    icon: <MoleculeIcon />,
  },
  {
    key: "alcoholByVolume",
    value: (value: number): string => (value ? `${value}%` : "-"),
    heading: "Alc. by volume",
    icon: <AlcoholIcon />,
  },
  {
    key: "closure",
    value: (value: string): string => value || "-",
    heading: "Closure",
    icon: <ClosureIcon />,
  },
  {
    key: "servingTemp",
    value: (value: string): string => value || "-",
    heading: "Serving temperature",
    icon: <TempIcon />,
  },
  {
    key: "volume",
    value: (value: string): string => value || "-",
    heading: "Bottle volume",
    icon: <VolumeIcon />,
  },
  {
    key: "blendInfo",
    value: (value: string): string => value || "-",
    heading: "Blend info",
    icon: <ShakerIcon />,
  },
];

export const WineWineNerd = ({
  heading,
  subheading,
  ...rest
}: WineWineNerdProps): JSX.Element => {
  return (
    <Section py={["50px", "50px", "60px"]} backgroundColor="#FFF">
      <Stack space={5}>
        <Stack space={2}>
          <Heading as="h3" variant="xxl" textAlign="center">
            {heading}
          </Heading>
          <Text textAlign="center">{subheading}</Text>
        </Stack>
        <Grid columns={[1, 4]} gutter={[0, 20]}>
          {items.map((item, i) => {
            const nerdItem = rest[item.key];
            if (item.value(nerdItem) === "-") return;

            const label: string = item.value(nerdItem);
            const isSpiritVintage = item.key === "vintage" && rest.isSpirit;
            return (
              <WineNerdBadge
                key={i}
                py={["10px", "0"]}
                borderTop={["1px solid", "none"]}
                borderColor={["divider"]}
                heading={isSpiritVintage ? "Aged" : item.heading}
                icon={isSpiritVintage ? <BarrelIcon /> : item.icon}
                label={label}
              />
            );
          })}
        </Grid>
      </Stack>
    </Section>
  );
};
